import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ArrowLeft, Loader2, CalendarDays, ChevronLeft, ChevronRight,
  FilterX, Clock, MapPin, Send, X
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { getUnidadCodigo } from './constants';
import EnvioMasivoModal from './EnvioMasivoModal';

const DIAS_SEMANA = ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const STORAGE_KEY = 'calendario_entregas_filtros';

const ESTADO_COLORS = {
  confirmada: { bg: '#dcfce7', border: '#86efac', text: '#166534', dot: '#22c55e' },
  por_confirmar: { bg: '#fff7ed', border: '#fdba74', text: '#9a3412', dot: '#f97316' },
  tentativa: { bg: '#eff6ff', border: '#93c5fd', text: '#1e40af', dot: '#3b82f6' },
};

const ESTADO_FECHA_OPTIONS = [
  { value: 'todos', label: 'Todos' },
  { value: 'confirmada', label: 'Confirmada' },
  { value: 'por_confirmar', label: 'Por confirmar' },
  { value: 'tentativa', label: 'Tentativa' },
];

const loadSavedFilters = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch { return null; }
};

const saveFilters = (f) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(f));
};

const CalendarioEntregas = () => {
  const navigate = useNavigate();
  const now = new Date();

  // Límite: 6 meses atrás
  const minDate = useMemo(() => {
    const d = new Date(now.getFullYear(), now.getMonth() - 6, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  }, []); // eslint-disable-line

  // State
  const [loading, setLoading] = useState(true);
  const [loadingCal, setLoadingCal] = useState(false);
  const [proyectos, setProyectos] = useState([]);
  const [arquitectos, setArquitectos] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [etapasLoading, setEtapasLoading] = useState(false);
  const [eventos, setEventos] = useState([]);
  const [semana, setSemana] = useState([]);

  // Filters
  const [filtroProyecto, setFiltroProyecto] = useState('');
  const [filtroEtapa, setFiltroEtapa] = useState('');
  const [filtroArquitecto, setFiltroArquitecto] = useState('todos');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [mesActual, setMesActual] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [filtersReady, setFiltersReady] = useState(false);
  const [showEnvioMasivo, setShowEnvioMasivo] = useState(false);
  const [dayModalData, setDayModalData] = useState(null);
  useEffect(() => {
    if (!dayModalData) return;
    const onKey = (e) => { if (e.key === 'Escape') setDayModalData(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dayModalData]);

  // Load initial filter options
  useEffect(() => {
    (async () => {
      try {
        const resp = await api.get('/entregas/calendario/filtros');
        const data = resp.data;
        setProyectos(data.proyectos || []);
        setArquitectos(data.arquitectos || []);

        const saved = loadSavedFilters();
        const esArqEntregas = data.usuario_es_arquitecto_entregas;
        const userId = data.usuario_id;
        const userArq = (data.arquitectos || []).find(a => a.id === userId);

        // Determine defaults
        let defaultProy = (data.proyectos || [])[0] || '';
        let defaultArq = 'todos';

        if (esArqEntregas && userArq) {
          defaultArq = userId;
        }

        // Use saved if valid
        if (saved) {
          if (saved.proyecto && (data.proyectos || []).includes(saved.proyecto)) defaultProy = saved.proyecto;
          if (saved.arquitecto) defaultArq = saved.arquitecto;
          if (saved.estado) setFiltroEstado(saved.estado);
          // etapa will be loaded after proyecto is set
        }

        setFiltroProyecto(defaultProy);
        setFiltroArquitecto(defaultArq);
        setFiltersReady(true);
      } catch {
        toast.error('Error cargando filtros');
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  // Load etapas when proyecto changes
  useEffect(() => {
    if (!filtroProyecto || !filtersReady || filtroProyecto === 'todos') {
      setEtapas([]);
      setFiltroEtapa('todas');
      return;
    }
    (async () => {
      setEtapasLoading(true);
      try {
        const resp = await api.get(`/entregas/calendario/etapas?proyecto_nombre=${encodeURIComponent(filtroProyecto)}`);
        const data = resp.data || [];
        setEtapas(data);

        const saved = loadSavedFilters();
        if (saved?.etapa && data.find(e => e.id === saved.etapa)) {
          setFiltroEtapa(saved.etapa);
        } else if (data.length > 0) {
          setFiltroEtapa('todas');
        } else {
          setFiltroEtapa('sin_etapas');
        }
      } catch {
        setEtapas([]);
        setFiltroEtapa('sin_etapas');
      } finally {
        setEtapasLoading(false);
      }
    })();
  }, [filtroProyecto, filtersReady]);

  // Persist filters
  useEffect(() => {
    if (!filtersReady) return;
    saveFilters({
      proyecto: filtroProyecto,
      etapa: filtroEtapa,
      arquitecto: filtroArquitecto,
      estado: filtroEstado,
    });
  }, [filtroProyecto, filtroEtapa, filtroArquitecto, filtroEstado, filtersReady]);

  // Load calendar events
  const loadEventos = useCallback(async () => {
    if (!filtersReady) return;
    setLoadingCal(true);
    try {
      const params = new URLSearchParams({
        year: mesActual.year.toString(),
        month: (mesActual.month + 1).toString(),
      });
      if (filtroProyecto && filtroProyecto !== 'todos') params.set('proyecto_nombre', filtroProyecto);
      if (filtroEtapa && filtroEtapa !== 'sin_etapas' && filtroEtapa !== 'todas') params.set('subdivision_id', filtroEtapa);
      if (filtroArquitecto && filtroArquitecto !== 'todos') params.set('arquitecto_id', filtroArquitecto);
      if (filtroEstado && filtroEstado !== 'todos') params.set('estado_fecha', filtroEstado);

      const resp = await api.get(`/entregas/calendario/eventos?${params.toString()}`);
      setEventos(resp.data || []);
    } catch {
      setEventos([]);
    } finally {
      setLoadingCal(false);
    }
  }, [mesActual, filtroProyecto, filtroEtapa, filtroArquitecto, filtroEstado, filtersReady]);

  // Load weekly sidebar
  const loadSemana = useCallback(async () => {
    if (!filtersReady) return;
    try {
      const params = new URLSearchParams();
      if (filtroProyecto && filtroProyecto !== 'todos') params.set('proyecto_nombre', filtroProyecto);
      if (filtroEtapa && filtroEtapa !== 'sin_etapas' && filtroEtapa !== 'todas') params.set('subdivision_id', filtroEtapa);
      if (filtroArquitecto && filtroArquitecto !== 'todos') params.set('arquitecto_id', filtroArquitecto);

      const resp = await api.get(`/entregas/calendario/semana-actual?${params.toString()}`);
      setSemana(resp.data || []);
    } catch {
      setSemana([]);
    }
  }, [filtroProyecto, filtroEtapa, filtroArquitecto, filtersReady]);

  useEffect(() => {
    loadEventos();
    loadSemana();
  }, [loadEventos, loadSemana]);

  // Calendar grid
  const diasDelMes = useMemo(() => {
    const first = new Date(mesActual.year, mesActual.month, 1);
    const lastDay = new Date(mesActual.year, mesActual.month + 1, 0).getDate();
    let startDay = first.getDay() - 1;
    if (startDay < 0) startDay = 6;
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= lastDay; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [mesActual]);

  const eventosPorDia = useMemo(() => {
    const map = {};
    eventos.forEach(ev => {
      const d = ev.fecha;
      if (!map[d]) map[d] = [];
      map[d].push(ev);
    });
    Object.values(map).forEach(arr =>
      arr.sort((a, b) => (a.hora_inicio || '').localeCompare(b.hora_inicio || ''))
    );
    return map;
  }, [eventos]);

  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const canGoBack = mesActual.year > minDate.year || (mesActual.year === minDate.year && mesActual.month > minDate.month);

  const navMes = (delta) => {
    setMesActual(prev => {
      let m = prev.month + delta;
      let y = prev.year;
      if (m < 0) { m = 11; y--; }
      if (m > 11) { m = 0; y++; }
      if (delta < 0 && (y < minDate.year || (y === minDate.year && m < minDate.month))) {
        return prev;
      }
      return { year: y, month: m };
    });
  };

  const goToday = () => {
    setMesActual({ year: now.getFullYear(), month: now.getMonth() });
  };

  const limpiarFiltros = () => {
    setFiltroProyecto('todos');
    setFiltroArquitecto('todos');
    setFiltroEstado('todos');
    localStorage.removeItem(STORAGE_KEY);
  };

  // Week date range for header
  const weekRange = useMemo(() => {
    const today = new Date();
    const fin = new Date(today);
    fin.setDate(today.getDate() + 7);
    const fmt = (d) => `${d.getDate()} ${MESES[d.getMonth()]?.substring(0, 3)}`;
    return `${fmt(today)} – ${fmt(fin)}`;
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-[#5B7FA6]" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="calendario-entregas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/entregas')} data-testid="btn-volver-entregas">
            <ArrowLeft size={16} className="mr-1" /> Volver
          </Button>
          <div>
            <h1 className="text-lg font-bold text-[#2D2D2D] flex items-center gap-2">
              <CalendarDays size={20} className="text-[#5B7FA6]" />
              Calendario de Entregas
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => setShowEnvioMasivo(true)}
            className="text-xs border-[#5B7FA6]/40 text-[#5B7FA6] hover:bg-[#5B7FA6]/5"
            data-testid="btn-envio-masivo"
          >
            <Send size={14} className="mr-1" /> Enviar propuestas masivas
          </Button>
          <Button variant="outline" size="sm" onClick={limpiarFiltros} className="text-xs" data-testid="btn-limpiar-filtros">
            <FilterX size={14} className="mr-1" /> Limpiar filtros
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-[#E8E8E7]">
        <CardContent className="p-3">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            {/* Proyecto */}
            <div data-testid="filtro-proyecto">
              <span className="text-[10px] text-[#888] font-medium uppercase tracking-wide">Proyecto</span>
              <Select value={filtroProyecto} onValueChange={setFiltroProyecto}>
                <SelectTrigger className="h-8 text-sm mt-0.5">
                  <SelectValue placeholder="Seleccionar proyecto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los proyectos</SelectItem>
                  {proyectos.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Etapa */}
            <div data-testid="filtro-etapa">
              <span className="text-[10px] text-[#888] font-medium uppercase tracking-wide">Etapa</span>
              <Select
                value={filtroEtapa}
                onValueChange={setFiltroEtapa}
                disabled={etapasLoading || filtroEtapa === 'sin_etapas'}
              >
                <SelectTrigger className="h-8 text-sm mt-0.5">
                  <SelectValue placeholder={etapasLoading ? 'Cargando...' : 'Sin etapas'} />
                </SelectTrigger>
                <SelectContent>
                  {etapas.length === 0 ? (
                    <SelectItem value="sin_etapas">Sin etapas</SelectItem>
                  ) : (
                    <>
                      <SelectItem value="todas">Todas las etapas</SelectItem>
                      {etapas.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Arquitecto */}
            <div data-testid="filtro-arquitecto">
              <span className="text-[10px] text-[#888] font-medium uppercase tracking-wide">Arquitecto</span>
              <Select value={filtroArquitecto} onValueChange={setFiltroArquitecto}>
                <SelectTrigger className="h-8 text-sm mt-0.5">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {arquitectos.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Estado Fecha */}
            <div data-testid="filtro-estado-fecha">
              <span className="text-[10px] text-[#888] font-medium uppercase tracking-wide">Estado de Fecha</span>
              <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                <SelectTrigger className="h-8 text-sm mt-0.5">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  {ESTADO_FECHA_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main grid: Calendar + Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Calendar */}
        <div className="lg:col-span-2">
          <Card className="border border-[#E8E8E7]" data-testid="calendario-grid">
            <CardContent className="p-4">
              {/* Month navigation */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline" size="icon" className="h-8 w-8"
                    onClick={() => navMes(-1)}
                    disabled={!canGoBack}
                    title={!canGoBack ? 'Máximo 6 meses atrás' : ''}
                    data-testid="btn-mes-anterior"
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <h2 className="text-lg font-bold text-[#2D2D2D] min-w-[180px] text-center">
                    {MESES[mesActual.month]} {mesActual.year}
                  </h2>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navMes(1)} data-testid="btn-mes-siguiente">
                    <ChevronRight size={16} />
                  </Button>
                </div>
                <Button
                  variant="outline" size="sm" onClick={goToday} className="text-xs"
                  disabled={mesActual.year === now.getFullYear() && mesActual.month === now.getMonth()}
                  data-testid="btn-hoy"
                >
                  Hoy
                </Button>
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 gap-px mb-px">
                {DIAS_SEMANA.map(d => (
                  <div key={d} className="text-center text-[11px] font-semibold text-[#888] py-2 bg-[#f8f8f8] rounded-t">{d}</div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="relative grid grid-cols-7 gap-px bg-[#e8e8e7]">
                {loadingCal && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
                    <Loader2 size={24} className="animate-spin text-[#5B7FA6]" />
                  </div>
                )}
                {diasDelMes.map((day, idx) => {
                  if (day === null) return <div key={`e-${idx}`} className="bg-[#fafafa] min-h-[90px]" />;
                  const fechaStr = `${mesActual.year}-${String(mesActual.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const evsDia = eventosPorDia[fechaStr] || [];
                  const isToday = fechaStr === todayStr;

                  return (
                    <div
                      key={day}
                      className={`bg-white min-h-[90px] p-1 relative ${isToday ? 'ring-2 ring-[#5B7FA6] ring-inset' : ''}`}
                      data-testid={`cal-day-${day}`}
                    >
                      <span className={`text-xs font-semibold ${isToday ? 'text-[#5B7FA6]' : 'text-[#555]'}`}>{day}</span>
                      <div className="mt-0.5 space-y-0.5">
                        {evsDia.slice(0, 3).map(ev => {
                          const colors = ESTADO_COLORS[ev.estado] || ESTADO_COLORS.tentativa;
                          return (
                            <div
                              key={ev.fecha_id}
                              className="flex items-center gap-0.5 cursor-default"
                              style={{
                                background: colors.bg,
                                borderLeft: `3px solid ${colors.dot}`,
                                borderRadius: '3px',
                                padding: '1px 4px',
                              }}
                              title={`${ev.codigo}${ev.unidad_codigo ? ' · ' + ev.unidad_codigo : ''} · ${ev.cliente_nombre} (${(ev.hora_inicio || '').substring(0,5)}-${(ev.hora_fin || '').substring(0,5)}) — ${ev.arquitecto_nombre || ''}\n${ev.proyecto_nombre || ''}${ev.subdivision_nombre ? ' · ' + ev.subdivision_nombre : ''}`}
                            >
                              <span className="text-[9px] font-medium truncate" style={{ color: colors.text }}>
                                {ev.codigo?.substring(0, 10)} · {(ev.cliente_nombre || '').split(',')[0].split(' ').pop()}
                              </span>
                            </div>
                          );
                        })}
                        {evsDia.length > 3 && (
                          <button
                            className="text-[8px] text-[#5B7FA6] font-medium hover:underline cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); setDayModalData({ fecha: fechaStr, eventos: evsDia }); }}
                            data-testid={`cal-more-${day}`}
                          >
                            +{evsDia.length - 3} más
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center gap-5 mt-3 pt-2 border-t border-[#E8E8E7]">
                {[
                  { label: 'Confirmada', color: '#22c55e' },
                  { label: 'Por confirmar', color: '#f97316' },
                  { label: 'Tentativa', color: '#3b82f6' },
                ].map(l => (
                  <div key={l.label} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: l.color }} />
                    <span className="text-[11px] text-[#888]">{l.label}</span>
                  </div>
                ))}
                <span className="ml-auto text-[10px] text-[#aaa]">
                  {eventos.length} evento{eventos.length !== 1 ? 's' : ''} en {MESES[mesActual.month]}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <Card className="border border-[#E8E8E7]" data-testid="semana-actual">
            <CardHeader className="pb-1">
              <CardTitle className="text-sm font-bold text-[#2D2D2D] flex items-center gap-1.5">
                <CalendarDays size={14} className="text-[#5B7FA6]" /> Próximas Entregas
              </CardTitle>
              <p className="text-[10px] text-[#888]">Próximos 7 días ({weekRange})</p>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto">
              {semana.length === 0 && (
                <p className="text-xs text-[#aaa] text-center py-6">No hay entregas confirmadas en los próximos 7 días</p>
              )}
              {semana.map(ent => {
                const fechaDate = new Date(ent.fecha + 'T12:00:00');
                const dia = fechaDate.getDate();
                const mesStr = MESES[fechaDate.getMonth()]?.substring(0, 3).toUpperCase();
                const diaSemana = ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'][fechaDate.getDay()];

                return (
                  <div
                    key={ent.id}
                    className="flex gap-3 p-2.5 rounded-lg border border-[#E8E8E7] cursor-pointer transition-all hover:shadow-sm hover:border-[#5B7FA6]/40"
                    onClick={() => navigate(`/entregas/${ent.id}`)}
                    data-testid={`semana-${ent.id}`}
                  >
                    <div className="shrink-0 w-11 h-12 rounded-lg bg-[#dcfce7] flex flex-col items-center justify-center">
                      <span className="text-base font-bold text-[#166534] leading-none">{dia}</span>
                      <span className="text-[9px] font-semibold text-[#166534] uppercase">{mesStr}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="font-mono text-xs font-semibold text-[#5B7FA6]">{ent.codigo}</span>
                        <Badge className="text-[9px] px-1.5 py-0 h-4 font-medium border-0 bg-[#dcfce7] text-[#166534]">
                          Confirmada
                        </Badge>
                      </div>
                      <p className="text-xs text-[#2D2D2D] font-medium truncate">{ent.proyecto_nombre}</p>
                      {ent.unidad_codigo && <p className="text-[11px] font-mono font-medium text-[#555] break-all" data-testid={`semana-unidad-${ent.codigo}`}>{ent.unidad_codigo}</p>}
                      <p className="text-[10px] text-[#888] truncate">{ent.cliente_nombre}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-[#5A5A5A] flex items-center gap-0.5">
                          <Clock size={9} className="text-[#888]" /> {diaSemana} {ent.hora}
                        </span>
                        {ent.arquitecto_nombre && (
                          <span className="text-[10px] text-[#888] flex items-center gap-0.5">
                            <MapPin size={9} /> {ent.arquitecto_nombre}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      </div>

      <EnvioMasivoModal
        open={showEnvioMasivo}
        onClose={() => setShowEnvioMasivo(false)}
        onComplete={() => { loadEventos(); loadSemana(); }}
      />

      {/* Modal: eventos del día */}
      {dayModalData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setDayModalData(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="day-events-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E8E8E7]">
              <div className="flex items-center gap-2">
                <CalendarDays size={16} className="text-[#5B7FA6]" />
                <h3 className="text-sm font-semibold text-[#2D2D2D]">
                  {(() => {
                    const [y, m, d] = dayModalData.fecha.split('-');
                    return `${parseInt(d)} de ${MESES[parseInt(m) - 1]} ${y}`;
                  })()}
                </h3>
                <Badge variant="outline" className="text-[10px]">{dayModalData.eventos.length} eventos</Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setDayModalData(null)} className="h-7 w-7 p-0">
                <X size={14} />
              </Button>
            </div>
            <div className="overflow-y-auto flex-1 px-5 py-3 space-y-2">
              {dayModalData.eventos.map(ev => {
                const colors = ESTADO_COLORS[ev.estado] || ESTADO_COLORS.tentativa;
                return (
                  <div
                    key={ev.fecha_id}
                    className="rounded-lg p-3 border"
                    style={{ backgroundColor: colors.bg, borderColor: colors.dot + '40' }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-bold font-mono" style={{ color: colors.dot }}>{ev.codigo}{ev.unidad_codigo && <span className="ml-1 font-normal text-[#888]" data-testid="unidad-codigo">· {ev.unidad_codigo}</span>}</span>
                      <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: colors.dot, color: '#fff' }}>
                        {ev.estado === 'confirmada' ? 'Confirmada' : ev.estado === 'por_confirmar' ? 'Por confirmar' : 'Tentativa'}
                      </Badge>
                    </div>
                    <p className="text-xs text-[#2D2D2D] font-medium mt-1">{ev.cliente_nombre}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[#666]">
                      <span className="flex items-center gap-0.5"><Clock size={9} /> {(ev.hora_inicio || '').substring(0,5)} - {(ev.hora_fin || '').substring(0,5)}</span>
                      {ev.arquitecto_nombre && <span className="flex items-center gap-0.5"><MapPin size={9} /> {ev.arquitecto_nombre}</span>}
                    </div>
                    <p className="text-[10px] text-[#888] mt-1">{ev.proyecto_nombre || ''}{ev.subdivision_nombre ? ' · ' + ev.subdivision_nombre : ''}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarioEntregas;
